/**
 * 画面のエラーダイアログに出すエラーの記録と、その外部ストア（#456）。
 *
 * @remarks
 * 利用者がダイアログのスクリーンショットを送れば原因を調べられるよう、1 件に「いつ・どの画面で・
 * どのリクエストが・何で失敗したか」とスタックトレースを全部持たせる。ダイアログ
 * （`components/errors/error-report-dialog.tsx`）は `useErrorReports` で読み、明示的に閉じるまで出し続ける。
 *
 * React の state ではなくモジュールに置くのは、`customFetch`・`window` のエラーハンドラなど
 * React の外からも積むため（操作ガイドの `lib/guides/store.ts` と同じ形）。積んだあとの処理
 * （ソースマップでスタックを元の位置に戻す・サーバーへログを送る）は `onErrorReported` で
 * 差し込む（`lib/errors/pipeline.ts`。テストではストアだけを使う）。
 */
import { useSyncExternalStore } from "react";

import { newLocalId } from "@/lib/local-id";

/** エラーの種類（ダイアログの「種類」の表示と、ログの `kind`）。 */
export type ErrorReportKind = "api" | "network" | "runtime" | "render";

/** 失敗した API リクエスト（パスとクエリ。ベース URL は含めない）。 */
export interface ErrorReportRequest {
  /** HTTP メソッド（大文字）。 */
  method: string;
  /** パス＋クエリ（例 `/api/v1/projects?limit=50`）。 */
  url: string;
}

/** ダイアログに出す 1 件。 */
export interface ErrorReport {
  /** ストア内の ID（表示の key）。 */
  id: string;
  /** 種類。 */
  kind: ErrorReportKind;
  /** 最初に起きた時刻。 */
  occurredAt: Date;
  /** 同じエラーが最後に起きた時刻（`count` が 2 以上のとき意味を持つ）。 */
  lastOccurredAt: Date;
  /** 同じエラーが起きた回数（SWR の再試行などで連発したものを 1 件にまとめる）。 */
  count: number;
  /** 説明（サーバーの `message`・例外の `message`）。 */
  message: string;
  /** 起きた画面（パス＋クエリ）。 */
  page: string;
  /** エラー ID（`X-Request-ID`。API・通信エラーだけ）。ログの突合キー。 */
  requestId: string | null;
  /** 失敗したリクエスト（API・通信エラーだけ）。 */
  request: ErrorReportRequest | null;
  /** HTTP ステータス（API エラーだけ。通信エラーは 0）。 */
  status: number | null;
  /** HTTP ステータスの説明（`Internal Server Error` など）。 */
  statusText: string | null;
  /** サーバーのエラーコード（`INTERNAL_ERROR`・`PROJECT_CLOSED` など）。 */
  code: string | null;
  /** サーバーがエラーを記録した時刻（応答の `timestamp`）。 */
  serverTimestamp: string | null;
  /** サーバーのスタックトレース（500 の応答の `error.stack`。呼び出し順）。 */
  serverStack: readonly string[] | null;
  /** サーバーの例外の型（500 の応答の `error.type`）。 */
  serverErrorType: string | null;
  /** 応答の本文が JSON でなかったときの先頭（ALB のエラーページなど）。 */
  bodyExcerpt: string | null;
  /** 例外の型名（`TypeError` など。画面側のエラーだけ）。 */
  errorName: string | null;
  /** 画面側のスタックトレース（ブラウザが出したまま＝本番は圧縮後の位置）。 */
  clientStack: string | null;
  /** 画面側のスタックトレースをソースマップで元の位置に戻したもの（戻せたときだけ）。 */
  resolvedClientStack: string | null;
  /** サーバー側の描画で起きた例外の digest（Next がサーバーのログと結ぶ ID）。 */
  digest: string | null;
}

/** {@link reportError} に渡す内容（ID・回数・時刻はストアが決める）。 */
export type ErrorReportInput = Pick<ErrorReport, "kind" | "message"> &
  Partial<
    Omit<ErrorReport, "id" | "kind" | "message" | "count" | "lastOccurredAt">
  >;

/** ダイアログに出す利用者と版（保護領域のレイアウトがサーバーから渡す）。 */
export interface ErrorReportEnvironment {
  /** ログイン中のメールアドレス。 */
  email: string | null;
  /** 配備した版（`APP_VERSION`）。 */
  version: string | null;
  /** 配備したコミット（`APP_COMMIT`）。 */
  commit: string | null;
}

/** ダイアログに残す件数の上限（古いものから捨てる）。 */
const MAX_REPORTS = 10;

/** この時間内に起きた同じエラーは 1 件にまとめる（SWR の再試行・フォーカス時の再検証）。 */
const DEDUPE_WINDOW_MS = 30_000;

const EMPTY: readonly ErrorReport[] = [];

let reports: readonly ErrorReport[] = EMPTY;
let environment: ErrorReportEnvironment = { email: null, version: null, commit: null };
const listeners = new Set<() => void>();
const reportedHandlers = new Set<(report: ErrorReport) => void>();

function emit() {
  for (const listener of listeners) listener();
}

function currentPage(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

function sameError(a: ErrorReport, b: ErrorReport): boolean {
  return (
    a.kind === b.kind &&
    a.message === b.message &&
    a.status === b.status &&
    a.code === b.code &&
    a.request?.method === b.request?.method &&
    a.request?.url === b.request?.url &&
    a.page === b.page
  );
}

/** {@link reportError} の動作の指定。 */
export interface ReportErrorOptions {
  /**
   * 新しく積んだとき {@link onErrorReported} の処理（スタックの復元・ログの送信）を呼ぶか（既定 true）。
   * トーストの「詳細」から開くときは false（起きたのは前で、ログは BE にある）。false のときは
   * 同じ失敗（エラー ID まで一致）が出ていればそれを見せるだけで回数を増やさず、内容が同じでも
   * エラー ID の違う失敗はまとめずに別の 1 件として積む。
   */
  notify?: boolean;
}

/**
 * エラーをダイアログに積む（同じエラーが続いたら回数だけ増やす）。
 *
 * @param input - 積む内容。`occurredAt`・`page` を省くと今の時刻・画面になる。
 * @param options - 動作の指定。
 * @returns 積んだ（またはまとめた先の）1 件。
 */
export function reportError(
  input: ErrorReportInput,
  options: ReportErrorOptions = {},
): ErrorReport {
  const notify = options.notify ?? true;
  const now = input.occurredAt ?? new Date();
  const report: ErrorReport = {
    id: newLocalId("err"),
    kind: input.kind,
    occurredAt: now,
    lastOccurredAt: now,
    count: 1,
    message: input.message,
    page: input.page ?? currentPage(),
    requestId: input.requestId ?? null,
    request: input.request ?? null,
    status: input.status ?? null,
    statusText: input.statusText ?? null,
    code: input.code ?? null,
    serverTimestamp: input.serverTimestamp ?? null,
    serverStack: input.serverStack ?? null,
    serverErrorType: input.serverErrorType ?? null,
    bodyExcerpt: input.bodyExcerpt ?? null,
    errorName: input.errorName ?? null,
    clientStack: input.clientStack ?? null,
    resolvedClientStack: input.resolvedClientStack ?? null,
    digest: input.digest ?? null,
  };
  if (!notify) {
    // 「詳細」から開くとき: 同じ失敗（エラー ID まで一致）が出ていればそれを見せ、無ければ新しく積む。
    // 内容が同じでもエラー ID の違う別の失敗にはまとめない（ログとの突き合わせを取り違えるため）
    const shown = reports.find(
      (r) => sameError(r, report) && r.requestId === report.requestId,
    );
    if (shown != null) return shown;
    reports = [report, ...reports].slice(0, MAX_REPORTS);
    emit();
    return report;
  }
  const existing = reports.find(
    (r) => sameError(r, report) && now.getTime() - r.lastOccurredAt.getTime() < DEDUPE_WINDOW_MS,
  );
  if (existing != null) {
    const merged = { ...existing, count: existing.count + 1, lastOccurredAt: now };
    reports = reports.map((r) => (r.id === existing.id ? merged : r));
    emit();
    return merged;
  }
  reports = [report, ...reports].slice(0, MAX_REPORTS);
  emit();
  for (const handler of reportedHandlers) handler(report);
  return report;
}

/**
 * 積んだ 1 件を書き換える（スタックを元の位置に戻せたときなど）。
 *
 * @param id - 対象の ID。
 * @param patch - 書き換える項目。
 */
export function updateErrorReport(
  id: string,
  patch: Partial<Omit<ErrorReport, "id">>,
): void {
  if (!reports.some((r) => r.id === id)) return;
  reports = reports.map((r) => (r.id === id ? { ...r, ...patch } : r));
  emit();
}

/** ダイアログを閉じる（積んだエラーを全部消す）。 */
export function dismissErrorReports(): void {
  if (reports.length === 0) return;
  reports = EMPTY;
  emit();
}

/** 今積まれているエラー（新しい順）。 */
export function getErrorReports(): readonly ErrorReport[] {
  return reports;
}

/**
 * 新しく積まれたエラーを受け取る処理を登録する（まとめられた 2 回目以降は呼ばない）。
 *
 * @param handler - 積まれた 1 件を受け取る関数。
 * @returns 登録を外す関数。
 */
export function onErrorReported(handler: (report: ErrorReport) => void): () => void {
  reportedHandlers.add(handler);
  return () => reportedHandlers.delete(handler);
}

/**
 * ダイアログに出す利用者と版を設定する。
 *
 * @param next - 利用者と版。
 */
export function setErrorReportEnvironment(next: ErrorReportEnvironment): void {
  environment = next;
  emit();
}

/** ダイアログに出す利用者と版。 */
export function getErrorReportEnvironment(): ErrorReportEnvironment {
  return environment;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 積まれているエラーを購読するフック（新しい順）。 */
export function useErrorReports(): readonly ErrorReport[] {
  return useSyncExternalStore(subscribe, getErrorReports, () => EMPTY);
}

/** ダイアログに出す利用者と版を購読するフック。 */
export function useErrorReportEnvironment(): ErrorReportEnvironment {
  return useSyncExternalStore(subscribe, getErrorReportEnvironment, getErrorReportEnvironment);
}
